import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import HeroSampleCard from "./HeroSampleCard";

interface HeroSectionProps {
  onGetStarted: () => void;
  totalRuns?: number | null;
  plan?: string;
}

const HeroSection = ({ onGetStarted, totalRuns, plan }: HeroSectionProps) => {
  const { t } = useLanguage();

  return (
    <section className="relative w-full flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-12 pt-10 pb-12 lg:min-h-screen lg:py-20">
      {/* Playful halos */}
      <div className="absolute -top-20 -left-20 w-[20rem] h-[20rem] sm:w-[24rem] sm:h-[24rem] rounded-full halo-yellow pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-[20rem] h-[20rem] sm:w-[24rem] sm:h-[24rem] rounded-full halo-mint pointer-events-none" />
      <div className="hidden lg:block absolute top-1/3 right-1/4 w-[18rem] h-[18rem] rounded-full halo-indigo pointer-events-none" />

      <div className="relative z-10 max-w-7xl w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
        {/* LEFT */}
        <div className="lg:col-span-6 space-y-5 lg:space-y-8 text-start order-1">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-start gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl border-2 border-foreground bg-bullish text-white shadow-brutal-sm"
          >
            <span className="text-base sm:text-lg leading-none mt-0.5">🛡️</span>
            <div className="leading-tight">
              <p className="text-[11px] sm:text-sm font-display font-extrabold uppercase tracking-wide">
                Beat the S&P 500 or get a full refund
              </p>
              <p className="text-[10px] sm:text-xs font-body font-medium opacity-90">
                Pro yearly | money-back guarantee in your first month
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border-2 border-foreground bg-accent shadow-brutal-sm"
          >
            <Sparkles className="w-3.5 h-3.5 text-foreground" />
            <span className="text-[10px] sm:text-xs uppercase tracking-wider font-bold text-foreground font-body">
              {t("hero.badge")}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-[2.25rem] leading-[1.05] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tighter text-foreground break-words"
          >
            {t("hero.title1")}{" "}
            <span className="relative inline-block">
              <span className="relative z-10 px-2 sm:px-3 bg-primary text-primary-foreground border-2 border-foreground shadow-brutal-sm">
                {t("hero.title2")}
              </span>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="max-w-md text-sm sm:text-lg text-foreground/80 leading-relaxed font-body font-medium"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-1 font-body"
          >
            <button
              onClick={onGetStarted}
              className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 sm:px-7 sm:py-4 bg-primary text-primary-foreground font-bold rounded-lg border-2 border-foreground shadow-brutal hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0_0_hsl(0_0%_0%)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-150"
            >
              {t("hero.cta")}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => {
                const el = document.getElementById("pricing");
                el?.scrollIntoView({ behavior: "smooth" });
              }}
              className="hidden sm:inline-flex items-center justify-center px-7 py-4 bg-card text-foreground font-bold rounded-lg border-2 border-foreground shadow-brutal-sm hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-brutal active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all duration-150"
            >
              {t("hero.viewPricing")}
            </button>
          </motion.div>

          {/* Stat strip */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-wrap items-stretch gap-2 sm:gap-3 pt-2 sm:pt-4"
          >
            <div className="flex flex-col px-3 py-2 sm:px-4 sm:py-3 bg-accent border-2 border-foreground rounded-lg shadow-brutal-sm">
              <span className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold font-body">Speed</span>
              <span className="text-xl sm:text-2xl font-display text-foreground">&lt; 1 min</span>
            </div>
            {totalRuns !== null && totalRuns !== undefined && totalRuns > 0 && (
              <div className="flex flex-col px-3 py-2 sm:px-4 sm:py-3 bg-secondary border-2 border-foreground rounded-lg shadow-brutal-sm">
                <span className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold font-body">Signals delivered</span>
                <span className="text-xl sm:text-2xl font-display text-foreground">{totalRuns.toLocaleString()}</span>
              </div>
            )}
            <div className="flex flex-col px-3 py-2 sm:px-4 sm:py-3 bg-card border-2 border-foreground rounded-lg shadow-brutal-sm">
              <span className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold font-body">No card</span>
              <span className="text-xl sm:text-2xl font-display text-foreground">Free pick</span>
            </div>
          </motion.div>
        </div>

        {/* RIGHT — daily sample report (shown first on mobile so users see instant value) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:col-span-6 w-full order-2 lg:order-2"
        >
          <HeroSampleCard />
          <p className="mt-3 sm:mt-4 text-center text-[10px] sm:text-[11px] text-foreground/60 uppercase tracking-wider font-bold font-body">
            {t("hero.powered")}
          </p>
        </motion.div>
      </div>
    </section>
  );
};


export default HeroSection;
